import React, { useEffect, useState } from 'react';
import {
	Alert,
	CircularProgress,
	FormControlLabel,
	FormGroup,
	IconButton,
	Switch,
	Tooltip,
	Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CheckroomIcon from '@mui/icons-material/Checkroom';
import {
	adminAreas,
	compareQuantities,
	formatterCurrency,
	handleConfirmModal,
	handleError,
	handleSuccess,
	handleWarning,
	money,
	rowStyle,
	whichGarment,
	whichStatus,
	whichStatusColor,
} from '../../helpers/utils';
import { useDispatch, useSelector } from 'react-redux';
import {
	deleteBias,
	getBiasInfo,
	getSortedBiases,
	setCurrentBias,
} from '../../actions/biases';
import { useNavigate } from 'react-router-dom';
import { DataGrid, gridClasses, esES } from '@mui/x-data-grid';
import { LinearWithValueLabel } from '../orders/ProgressBar';
import { BiasInfo } from './DialogBiasInfo/BiasInfo';
import {
	getIndividualAreas,
	setIndividualAreas,
} from '../../actions/individualAreas';
import Label from '../utils/Label';
import { formatDateMX } from '../../helpers/hours';
import { BiasSizesDialog } from './DialogBiasInfo/BiasSizesDialog';
import { HistoryTimelineModal } from '../tracking/HistoryTimeLine';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';

const AlertStyle = { margin: '10px 0', fontSize: '18px' };

export const columnStyleCell = {
	headerClassName: 'column-header-pilu',
	headerAlign: 'center',
	align: 'center',
};

export const BiasTable = ({
	order = null,
	refreshOrder = null,
	header = null,
	totalAmount = 0,
	setTotalAmount = null,
	idOrder = null,
}) => {
	// Hook for navigation
	const navigate = useNavigate();

	// Dispatch
	const dispatch = useDispatch();

	// Store
	const { user } = useSelector((state) => state);
	const { area: areas = [] } = useSelector((state) => state.catalogs);

	// Rows
	const [rows, setRows] = useState([]);

	// Sort criteria
	const [sortCriteria, setSortCriteria] = useState('original');

	// Handle change criteria
	// const handleChangeCriteria = (event) => {
	// 	setSortCriteria(event.target.value);
	// };

	// Current row id
	const [currentRowId, setCurrentRowId] = useState(1);

	// Loading state
	const [isLoading, setLoading] = useState(false);

	// Datagrid view
	const [datagridFullView, setDatagridFullView] = useState(true);

	// Current bias
	const [currentBias, setCurrentBias2] = useState(null);

	// Full-screen dialog
	const [open, setOpen] = React.useState(false);
	const handleClickOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);

	// Sizes dialog
	const [openSizesDialog, setOpenSizesDialog] = React.useState(false);
	const handleOpenSizesDialog = () => setOpenSizesDialog(true);
	const handleCloseSizesDialog = () => setOpenSizesDialog(false);

	// Individual Areas
	const [individualAreas, setIndividualAreas2] = useState([]);

	// Generate rows by array
	const generateRows = async (newData = false) => {
		// New biases
		let newBiases = [];

		setLoading(true);

		if (newData) {
			// I'M USING GIVEN DATA
			newBiases = newData;
		} else {
			// I'M USING THE SERVER!
			const { data } = await getSortedBiases(
				idOrder,
				sortCriteria,
				user.area?.name
			);
			newBiases = data.results;
		}

		if (newBiases?.length === 0) {
			setRows([]);
		} else {
			// Calculate subtotal, iva and total
			const subtotal_global =
				newBiases.reduce(
					(sum, { total_price }) => sum + total_price,
					0 //subtotal,
				) || 0;

			const total_price_global = subtotal_global; //* 1.16;

			// Accumulate total amount
			setTotalAmount(total_price_global);

			const res = newBiases.map((item, index) => {
				const row = {
					...item,
					rowID: index + 1,
					progress: parseInt(item.progress),
					unit_price: `${formatterCurrency(item.unit_price)}`,
					subtotal: `${formatterCurrency(item.subtotal)}`,
					total_price: `${formatterCurrency(item.total_price)}`,
					status: {
						key: item.status,
						name: whichStatus(item.status),
						color: whichStatusColor(item.status),
					},
					garment: {
						...item.garment,
						test: item?.garment?.type ?? '-',
						type: item?.garment?.type ? whichGarment(item?.garment?.type) : 'Sin tipo',
					},
					delivery_forecast: formatDateMX(item?.delivery_forecast),
				};

				// If user is an operator
				if (
					user?.user_type?.name === 'Operador' &&
					!adminAreas.includes(user?.area?.name)
				) {
					row.unit_price = '$----';
					row.subtotal = '$----';
					row.total_price = '$----';
				}

				return row;
			});
			setRows(res);
		}

		setLoading(false);
	};

	useEffect(() => {
		// Set sort criteria in local storage
		// if (!localStorage.getItem('lastPathPilu').includes('new-bias')) {
		// Reset local storage current biases
		localStorage.setItem('currentBiases', JSON.stringify(null));
		// } else {
		// localStorage.setItem('lastPathPilu', null);
		// }
		localStorage.setItem('sortCriteriaBiases', sortCriteria);
		// Generate rows
		generateRows();
	}, [sortCriteria, idOrder]);

	const handleGetIndividualAreas = (id = null) => {
		// Get individual areas
		getIndividualAreas(id ?? currentBias.id)
			.then(({ data }) => {
				try {
					setIndividualAreas2(data.results);
					dispatch(setIndividualAreas(data.results));
				} catch (error) {
					// Redux mutation error
					console.log(error);
				}
			})
			.catch((error) => {
				handleError(
					error,
					'Ocurrió un error al intentar obtener las áreas individuales'
				);
			});
	};

	useEffect(() => {
		if (currentBias) {
			handleGetIndividualAreas();
		}
	}, []);

	// Handle delete user with modal confirmation
	const handleDelete = (id) => {
		handleConfirmModal(
			'Precaución',
			'¿Seguro que deseas borrar la partida?',
			'warning',
			'Sí, borrar'
		)
			.then(({ isConfirmed }) => {
				if (isConfirmed) {
					deleteBias(id)
						.then(({ data }) => {
							handleSuccess('Partida eliminada correctamente.');
							generateRows();
						})
						.catch((error) => {
							handleError(
								error,
								'No se pudo eliminar la partida.'
							);
						});
				}
			})
			.catch((error) => handleError(error));
	};

	// Check if the amounts match
	const checkAmountMatch = () =>
		compareQuantities(order?.amount?.number, totalAmount);

	const handleViewBiasDetail = (id) => {
		// Get info
		getBiasInfo(id).then(({ data }) => {
			// Get dispatch
			dispatch(setCurrentBias(data));
			// Set info
			setCurrentBias2(data);
			// Open dialog
			handleClickOpen();
		});
	};

	// Handle get and set bias info by id
	const handleGetBiasInfo = (id) => {
		if (checkAmountMatch()) {
			handleViewBiasDetail(id);
		} else {
			handleConfirmModal(
				'Precaución',
				`El importe de la orden (${
					order?.amount?.formatted
				}) no coincide con la suma de los importes de las partidas (${money(
					totalAmount
				)}). Consulte al jefe de área.`,
				'warning',
				'OK',
				false
			).then(({ isConfirmed }) => {
				if (isConfirmed) {
					handleViewBiasDetail(id);
				}
			});
		}
	};

	const handleCheckLogisticsIsDone = (row) => {
		if (user?.area?.name === 'Logística' || row?.logistics_is_done) {
			handleOpenBiasInfo(row);
		} else {
			handleWarning('Logística no ha validado esta partida.');
		}
	};

	const Cell = ({ subtitle, text = '', lastBreaks = true }) => (
		<>
			<b>{subtitle}:</b>
			<br />
			{text ? (text === 'No lleva' ? '' : text) : 'No se asignó'}
			{lastBreaks && <br />}
		</>
	);

	const iconButtonStyle = { color: '#143f6c' };

	const itemIconButton = { width: '50%', margin: '0 auto' };

	const handleOpenBiasInfo = (row) => {
		setCurrentRowId(row.rowID);
		handleGetBiasInfo(row?.id);
	};

	const [currentRow, setCurrentRow] = useState(null); // Para mantener el estado de la fila actual

	const [modalOpen, setModalOpen] = useState(false);

	const handleOpenModal = () => {
		setModalOpen(true);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
	};

	// Generic data
	const historyList = [
		{
			is_delayed: false,
			entryDate: '2024-04-01',
			almacen: 'Almacén 1',
			corte: 'Corte 1',
			maquila: 'Maquila 1',
			acabado: 'Acabado 1',
			embarque: 'Embarque 1',
			empaque: 'Empaque 1',
			authorizedDays: 10,
			occupiedDays: 5,
			lastUpdate: '2024-04-20',
		},
		{
			is_delayed: true,
			entryDate: '2024-03-15',
			almacen: 'Almacén 2',
			corte: 'Corte 2',
			maquila: 'Maquila 2',
			acabado: 'Acabado 2',
			embarque: 'Embarque 2',
			empaque: 'Empaque 2',
			authorizedDays: 7,
			occupiedDays: 7,
			lastUpdate: '2024-04-18',
		},
		{
			is_delayed: true,
			entryDate: '2024-03-15',
			almacen: 'Almacén 2',
			corte: 'Corte 2',
			maquila: 'Maquila 2',
			acabado: 'Acabado 2',
			embarque: 'Embarque 2',
			empaque: 'Empaque 2',
			authorizedDays: 7,
			occupiedDays: 7,
			lastUpdate: '2024-04-18',
		},
		{
			is_delayed: true,
			entryDate: '2024-03-15',
			almacen: 'Almacén 2',
			corte: 'Corte 2',
			maquila: 'Maquila 2',
			acabado: 'Acabado 2',
			embarque: 'Embarque 2',
			empaque: 'Empaque 2',
			authorizedDays: 7,
			occupiedDays: 7,
			lastUpdate: '2024-04-18',
		},
		{
			is_delayed: true,
			entryDate: '2024-03-15',
			almacen: 'Almacén 2',
			corte: 'Corte 2',
			maquila: 'Maquila 2',
			acabado: 'Acabado 2',
			embarque: 'Embarque 2',
			empaque: 'Empaque 2',
			authorizedDays: 7,
			occupiedDays: 7,
			lastUpdate: '2024-04-18',
		},
	];

	// Define columns with full info
	const columnsFullInfo = [
		{
			field: 'rowID',
			headerName: '#',
			...columnStyleCell,
			width: 15,
			renderCell: ({ row }) => (
				<div style={{ margin: '0 auto' }}>{row.rowID}</div>
			),
		},
		{
			field: 'actions',
			headerName: 'Acciones',
			...columnStyleCell,
			width: 100,
			// Add actions to each case
			renderCell: ({ row }) => (
				<div
					style={{
						display: 'flex',
						flexDirection: 'column',
						width: '100%',
					}}
				>
					<div style={itemIconButton}>
						<Tooltip title='Ver información' placement='left-end'>
							<IconButton
								style={iconButtonStyle}
								aria-label='profile'
								color='primary'
								onClick={() => handleCheckLogisticsIsDone(row)}
							>
								<InfoIcon />
							</IconButton>
						</Tooltip>
					</div>
					<div style={itemIconButton}>
						<Tooltip title='Ver tallas' placement='left-end'>
							<IconButton
								style={iconButtonStyle}
								aria-label='sizes'
								color='primary'
								onClick={() => {
									setCurrentBias2(row);
									handleOpenSizesDialog();
								}}
							>
								<CheckroomIcon />
							</IconButton>
						</Tooltip>
					</div>
					<div style={itemIconButton}>
						{/* Mesa de control or Asesor */}
						{(user?.area?.id === 1 ||
							[1, 2, 3, 5].includes(user?.user_type?.id)) && (
							<Tooltip title='Editar' placement='left-end'>
								<IconButton
									style={iconButtonStyle}
									aria-label='edit'
									color='primary'
									onClick={() => {
										const path = `/dashboard/order/${idOrder}/edit-bias/${row?.id}`;
										localStorage.setItem(
											'currentPathPilu',
											path
										);
										navigate(path);
									}}
								>
									<EditIcon />
								</IconButton>
							</Tooltip>
						)}
					</div>
					<div style={itemIconButton}>
						{/* Mesa de control or Asesor */}
						{(user?.area?.id === 1 ||
							[1, 2, 3, 5].includes(user?.user_type?.id)) && (
							<Tooltip title='Eliminar' placement='left-end'>
								<IconButton
									style={iconButtonStyle}
									aria-label='delete'
									color='primary'
									onClick={() => handleDelete(row?.id)}
								>
									<DeleteIcon />
								</IconButton>
							</Tooltip>
						)}
					</div>
					<div style={itemIconButton}>
						<Tooltip title='Ver historial' placement='left-end'>
							<IconButton
								style={iconButtonStyle}
								aria-label='profile'
								color='primary'
								onClick={handleOpenModal}
							>
								<FormatListBulletedIcon />
							</IconButton>
						</Tooltip>
					</div>
				</div>
			),
		},
		{
			field: 'logistics_is_done',
			headerName: 'Logística completada',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell
						subtitle='Estatus'
						text='No lleva'
						lastBreaks={false}
					/>
					{/* Status */}
					<Label
						color={row?.logistics_is_done ? 'success' : 'warning'}
						sx={{
							width: '100%',
							margin: '5px 0',
						}}
					>
						{row?.logistics_is_done
							? 'Completado'
							: 'Falta por revisar'}
					</Label>
				</span>
			),
		},
		{
			field: 'process_area',
			headerName: `Mis procesos pendientes (${
				user?.area?.name ?? 'No es operador'
			})`,
			...columnStyleCell,
			width: 250,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{row?.process_area?.area_required ? (
						<ul>
							{row?.process_area?.subbiases_status?.map(
								(item) => (
									<li key={item?.subbias}>
										<b>{item?.subbias}</b>
										<br />
										<Label
											color={
												item?.status === 'No empezado'
													? 'error'
													: 'warning'
											}
											sx={{
												width: '100%',
												margin: '5px 0',
											}}
										>
											{item?.status}
										</Label>
									</li>
								)
							)}
						</ul>
					) : (
						'No se requieren procesos de esta área.'
					)}
				</span>
			),
		},
		{
			field: 'garment',
			headerName: 'Prenda',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell subtitle='Nombre' text={row?.garment?.name} />
					<Cell subtitle='Tipo' text={row?.garment?.type} />
					<Cell
						subtitle='Clasificación'
						text={row?.garment?.classification}
					/>
				</span>
			),
		},
		{
			field: 'variant',
			headerName: 'Variante',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell subtitle='Nombre' text={row?.variant?.name} />
					<Cell subtitle='Estilo' text={row?.variant?.style} />
				</span>
			),
		},
		{
			field: 'fabric',
			headerName: 'Tela',
			...columnStyleCell,
			width: 260,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell subtitle='Tipo' text={row?.fabric?.name} />
					<Cell subtitle='Color' text={row?.fabric?.color} />
					{row?.fabric?.width.length > 0 && (
						<Cell subtitle='Ancho' text={row?.fabric?.width} />
					)}
					{row?.fabric?.composition.length > 0 && (
						<Cell
							subtitle='Composición'
							text={row?.fabric?.composition}
						/>
					)}
				</span>
			),
		},
		{
			field: 'embroidery',
			headerName: 'Bordado',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell subtitle='Espalda' text={row?.back_embroidery} />
					<Cell subtitle='Frente' text={row?.front_embroidery} />
					<Cell subtitle='Mangas' text={row?.sleeves_embroidery} />
					<Cell subtitle='Tipo' text={row?.embroidery_type} />
					<Cell subtitle='Color' text={row?.embroidery_color} />
					{row?.measures_embroidery?.length > 0 && (
						<Cell
							subtitle='Medidas'
							text={row?.measures_embroidery}
						/>
					)}
				</span>
			),
		},
		{
			field: 'stamped',
			headerName: 'Estampado',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell subtitle='Espalda' text={row?.back_stamped} />
					<Cell subtitle='Frente' text={row?.front_stamped} />
					<Cell subtitle='Mangas' text={row?.sleeves_stamped} />
					<Cell subtitle='Tipo de tinte' text={row?.stamped_type} />
					<Cell subtitle='Color' text={row?.stamped_color} />
					{row?.measures_stamped?.length > 0 && (
						<Cell subtitle='Medidas' text={row?.measures_stamped} />
					)}
				</span>
			),
		},
		{
			field: 'variable',
			headerName: 'Variables',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{row?.variable?.length > 0 ? (
						<ul>
							{row?.variable.map((item) => (
								<li key={`variable-${item?.id || ''}`}>
									{item?.name || ''}
								</li>
							)) || ''}
						</ul>
					) : (
						'No se escogieron variables'
					)}
				</span>
			),
		},
		{
			field: 'key1',
			headerName: 'Seguimiento',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell
						subtitle='Estatus'
						text='No lleva'
						lastBreaks={false}
					/>
					{/* Status */}
					<Label
						color={row?.status?.color}
						sx={{
							width: '100%',
							margin: '5px 0',
						}}
					>
						{row?.status?.name}
					</Label>
					<Cell
						subtitle='Progreso'
						text='No lleva'
						lastBreaks={false}
					/>
					<LinearWithValueLabel progress={row?.progress} />
					<Cell
						subtitle='Fecha estimada'
						text={row?.delivery_forecast}
					/>
				</span>
			),
		},
		{
			field: 'key2',
			headerName: 'Detalle',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					<Cell subtitle='Clave' text={row?.key} />
					<Cell subtitle='Unidades' text={row?.units} />
					<Cell subtitle='Precio unitario' text={row?.unit_price} />
					<Cell subtitle='Total' text={row?.total_price} />
				</span>
			),
		},
	];

	// Define columns with less info
	const columnsLessInfo = [
		{
			field: 'rowID',
			headerName: '#',
			...columnStyleCell,
			width: 15,
			renderCell: ({ row }) => (
				<div style={{ margin: '0 auto' }}>{row.rowID}</div>
			),
		},
		{
			field: 'actions',
			headerName: 'Acciones',
			width: 200,
			// Add actions to each case
			renderCell: ({ row }) => (
				<div
					style={{
						display: 'flex',
						flexDirection: 'row',
						margin: '0 auto',
						width: '100%',
					}}
				>
					<div style={itemIconButton}>
						<Tooltip title='Ver información' placement='left-end'>
							<IconButton
								style={iconButtonStyle}
								aria-label='profile'
								color='primary'
								onClick={() => handleCheckLogisticsIsDone(row)}
							>
								<InfoIcon />
							</IconButton>
						</Tooltip>
					</div>
					<div style={itemIconButton}>
						<Tooltip title='Ver tallas' placement='left-end'>
							<IconButton
								style={iconButtonStyle}
								aria-label='sizes'
								color='primary'
								onClick={() => {
									setCurrentBias2(row);
									handleOpenSizesDialog();
								}}
							>
								<CheckroomIcon />
							</IconButton>
						</Tooltip>
					</div>
					<div style={itemIconButton}>
						{/* Mesa de control or Asesor */}
						{(user?.area?.id === 1 ||
							[1, 2, 3, 5].includes(user?.user_type?.id)) && (
							<Tooltip title='Editar' placement='left-end'>
								<IconButton
									style={iconButtonStyle}
									aria-label='edit'
									color='primary'
									onClick={() => {
										const path = `/dashboard/order/${idOrder}/edit-bias/${row?.id}`;
										localStorage.setItem(
											'currentPathPilu',
											path
										);
										navigate(path);
									}}
								>
									<EditIcon />
								</IconButton>
							</Tooltip>
						)}
					</div>
					<div style={itemIconButton}>
						{/* Mesa de control or Asesor */}
						{(user?.area?.id === 1 ||
							[1, 2, 3, 5].includes(user?.user_type?.id)) && (
							<Tooltip title='Eliminar' placement='left-end'>
								<IconButton
									style={iconButtonStyle}
									aria-label='delete'
									color='primary'
									onClick={() => handleDelete(row?.id)}
								>
									<DeleteIcon />
								</IconButton>
							</Tooltip>
						)}
					</div>
					<div style={itemIconButton}>
						<Tooltip title='Ver historial' placement='left-end'>
							<IconButton
								style={iconButtonStyle}
								aria-label='profile'
								color='primary'
								onClick={handleOpenModal}
							>
								<FormatListBulletedIcon />
							</IconButton>
						</Tooltip>
					</div>
				</div>
			),
		},
		{
			field: 'key3',
			headerName: 'Fecha estimada',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{row?.delivery_forecast}
				</span>
			),
		},
		{
			field: 'key4',
			headerName: 'Clave',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>{row?.key}</span>
			),
		},
		{
			field: 'garment',
			headerName: 'Prenda',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>{row?.garment?.name}</span>
			),
		},
		{
			field: 'variant',
			headerName: 'Variante',
			...columnStyleCell,
			width: 150,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{row?.variant?.style || ''}
				</span>
			),
		},
		{
			field: 'fabric',
			headerName: 'Tela',
			...columnStyleCell,
			width: 260,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>{row?.fabric?.name}</span>
			),
		},
		{
			field: 'key1',
			headerName: 'Estatus',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<span className='table-cell-trucate'>
					{/* Status */}
					<Label
						color={row?.status?.color}
						sx={{
							width: '100%',
							margin: '5px 0',
						}}
					>
						{row?.status?.name}
					</Label>
				</span>
			),
		},
		{
			field: 'key2',
			headerName: 'Progreso',
			...columnStyleCell,
			width: 180,
			renderCell: ({ row }) => (
				<LinearWithValueLabel progress={row?.progress} />
			),
		},
	];

	if (
		!order?.released &&
		user?.user_type?.name === 'Operador' &&
		!adminAreas.includes(user?.area?.name)
	) {
		return (
			<Alert severity='warning' style={AlertStyle}>
				<b>
					No se pueden agregar procesos porque la orden{' '}
					<u>aún no ha sido liberada</u>.
				</b>
			</Alert>
		);
	}

	if (order?.status?.key === 'ICF' && user?.area?.name === 'Logística') {
		return (
			<Alert severity='warning' style={AlertStyle}>
				<b>
					Aún no puedes <u>liberar</u> el pedido porque Mesa de
					control aún no lo ha <u>validado</u>.
				</b>
			</Alert>
		);
	}

	return (
		<div>
			{/* Modal HistoryTimeLineModal */}
			<HistoryTimelineModal
				open={modalOpen}
				onClose={handleCloseModal}
				historyList={historyList}
			/>
			{/* Full-screen dialog */}
			{currentBias && (
				<>
					<BiasInfo
						open={open}
						handleGetIndividualAreas={handleGetIndividualAreas}
						handleClose={handleClose}
						bias={currentBias}
						areas={areas}
						setCurrentBias2={setCurrentBias2}
						individualAreas={individualAreas}
						order={order}
						refreshOrder={refreshOrder}
						refreshBias={handleGetBiasInfo}
						refreshBiasTable={generateRows}
						rowId={currentRowId}
						setCurrentRowId={setCurrentRowId}
						biases={rows || []}
						numberOfBiases={rows?.length || 0}
					/>
				</>
			)}

			{/* Sizes dialog */}
			<BiasSizesDialog
				open={openSizesDialog}
				handleClose={handleCloseSizesDialog}
				bias={currentBias}
			/>

			<Typography
				variant='h6'
				gutterBottom
				style={{ ...rowStyle, margin: '20px 0 0 0' }}
			>
				Partidas ({rows.length})
				{(user?.user_type?.name !== 'Operador' ||
					user?.area?.name === 'Mesa de Control') && (
					<>
						{' - '}Costo total: {money(totalAmount)}
					</>
				)}
				<div style={{ marginLeft: '25px' }}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									checked={datagridFullView}
									onChange={() =>
										setDatagridFullView(!datagridFullView)
									}
								/>
							}
							label='Tabla completa'
						/>
					</FormGroup>
				</div>
				<div style={{ marginLeft: '10px' }}>
					<FormGroup>
						<FormControlLabel
							control={
								<Switch
									checked={header.showHeader}
									onChange={() =>
										header.setShowHeader(!header.showHeader)
									}
								/>
							}
							label='Mostrar encabezado'
						/>
					</FormGroup>
				</div>
				{/* SORTING */}
				{/* <div style={{ marginLeft: '10px' }}>
					<Box sx={{ minWidth: 160 }}>
						<FormControl variant='standard' fullWidth>
							<InputLabel id='sort-criteria-label'>
								Criterio de ordenamiento
							</InputLabel>
							<Select
								labelId='sort-criteria-label'
								id='sort-criteria'
								value={sortCriteria}
								label='Criterio de ordenamiento'
								onChange={handleChangeCriteria}
							>
								<MenuItem value='original'>Original</MenuItem>
								<MenuItem value='variant'>Variante</MenuItem>
								<MenuItem value='fabric'>Tela</MenuItem>
								<MenuItem value='unit_price'>
									Precio unitario
								</MenuItem>
								<MenuItem value='units'>Volumen</MenuItem>
							</Select>
						</FormControl>
					</Box>
				</div> */}
			</Typography>

			<br />

			{isLoading ? (
				<center style={{ padding: '20%' }}>
					<CircularProgress />
				</center>
			) : (
				<>
					{/* Datagrid table */}
					{rows.length > 0 ? (
						<DataGrid
							sx={{
								'& .MuiDataGrid-cell': {
									display: 'flex',
									alignItems: datagridFullView
										? 'flex-start'
										: 'center',
									justifyContent: datagridFullView
										? 'left'
										: 'center',
									padding: datagridFullView ? '10px' : '0',
								},
							}}
							style={{ height: '600px', margin: '10px 0 0 0' }}
							density='compact'
							rows={rows}
							getRowHeight={() => 'auto'}
							getRowClassName={(params) =>
								params.indexRelativeToCurrentPage % 2 === 0
									? 'even'
									: 'odd'
							}
							columns={
								datagridFullView
									? columnsFullInfo
									: columnsLessInfo
							}
							localeText={
								(gridClasses,
								esES.components.MuiDataGrid.defaultProps
									.localeText)
							}
						/>
					) : (
						<Alert severity='info' style={{ margin: '10px 0' }}>
							<b>
								No hay ninguna partida.
								<br />
								<br />
								{/* Mesa de control or Asesor */}
								{user?.area?.id === 1 ||
								[1, 2, 3, 5].includes(user?.user_type?.id) ? (
									<>
										Puede agregar partidas haciendo clic en
										el ícono{' '}
										<CheckroomIcon
											style={{
												width: '25px',
												margin: '-7px 0 0 0',
											}}
										/>
										.
									</>
								) : (
									<>
										Solo los asesores y mesa de control
										pueden agregar partidas.
									</>
								)}
							</b>
						</Alert>
					)}
				</>
			)}
		</div>
	);
};
